import Modal from "@/components/Common/Modal";
import useFetchUserFromAuthToken from "@/hooks/useFetchUserFromAuthToken";
import {useIsLoggedIn, useSelectedCompany} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import BrandLogo from "@/pages/profile/BrandLogo";
import RegistrationBottomView from "@/pages/registerCompany/RegistrationBottomView";
import RegistrationTopView from "@/pages/registerCompany/RegistrationTopView";
import CompanyDetailsStep from "@/pages/registerCompany/steps/CompanyDetailsStep";
import CredentialsStep from "@/pages/registerCompany/steps/CredentialsStep";
import InterestSelectionStep from "@/pages/registerCompany/steps/InterestSelectionStep";
import useRegistrationStore from "@/store/registration";
import useUserStore from "@/store/user";
import {useTranslation} from "react-i18next";
import {useEffectOnce} from "react-use";

const RegistrationModal = () => {
  const {t} = useTranslation(["verify_account", "common"]);
  const isLoggedIn = useIsLoggedIn();
  const user = useUserStore(state => state.user);
  const selectedCompany = useSelectedCompany();
  const step = useRegistrationStore(state => state.step);
  const setValue = useRegistrationStore(state => state.setValue);

  const {isLoading} = useFetchUserFromAuthToken(data => {
    setValue("isRegisteringAfterDirectPurchase", true);
    setValue("isEmailPrefilled", true);
    setValue("companyId", Number(data.sub_brands?.[0]?.company?.id));
    setValue("userId", Number(data.id));
    setValue("email", data.email!);
    setValue("package_subscription", data.package_subscription);
  });

  useEffectOnce(() => {
    // This flow shares its registration state with the invite flow; clear any
    // invite-sourced prefill so finishing registration never inherits the invite email.
    setValue("isEmailPrefilled", false);
    setValue("email", "");
    setValue("companyId", undefined);

    if (isLoggedIn) {
      setValue("isRegisteringAfterDirectPurchase", true);
      setValue("isEmailPrefilled", true);
      setValue("companyId", Number(selectedCompany?.id));
      setValue("userId", Number(user.id));
      setValue("email", user.email!);
    }
  });

  if (isLoading) {
    return null;
  }

  return (
    <Modal
      className="p-0"
      onClose={() => undefined}
      customTitleRow={<></>}
      customBottomRow={<></>}
      shouldHideCloseButton>
      <div
        data-testid="finish-registration-modal"
        className={clsxm(
          "flex w-full flex-col items-center gap-10 self-center",
          step !== "credentials" && "max-w-182.5 px-4 xl:px-0",
        )}>
        <h2 className="text-neutral-black my-0 self-stretch pt-5 text-center text-4xl font-bold sm:text-5xl">
          {t("finish_setting_up_your_account")}
        </h2>
        <div className={clsxm("w-full max-w-182.5 self-center", step === "credentials" && "px-4 xl:px-0")}>
          <RegistrationTopView isRefluencedLogoVisible={false} />
        </div>
        {step === "company_details" && (
          <div className="flex flex-col items-center gap-5 self-stretch">
            <span className="text-neutral-black text-xl font-semibold">{t("common:addCompanyLogo")}</span>
            <BrandLogo />
          </div>
        )}
        <div className="self-stretch">
          {step === "credentials" && (
            <CredentialsStep
              className="mx-auto max-w-182.5 px-4 py-10 xl:px-0"
              onboardingClassName="max-w-182.5 self-center px-4 sm:px-4 lg:px-4 xl:px-0"
            />
          )}
          {step === "company_details" && <CompanyDetailsStep />}
          {step === "interest_selection" && <InterestSelectionStep />}
        </div>
        <div className="self-stretch pb-5">
          <RegistrationBottomView isInsideModal />
        </div>
      </div>
    </Modal>
  );
};

export default RegistrationModal;
